import moment from 'moment';
import { Col, Form, Row } from 'antd';
import type { RangeValue } from 'rc-picker/lib/interface';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import CloseButton from 'components/new-ui/composed/modal/filters-modal/CloseButton';
import FiltersModal from 'components/new-ui/composed/modal/filters-modal/FiltersModal';
import { JobStatuses } from 'store/jobs/job';
import RangePicker from 'components/new-ui/base/date-picker/RangePicker';
import IndividualSelect from 'components/new-ui/composed/selects/IndividualSelect';
import TeamSelect from 'components/new-ui/composed/selects/TeamSelect';
import CustomerSelect from 'components/new-ui/composed/selects/CustomerSelect';
import { JobParamsKeys } from '../useJobsParams';
import JobStatusSelect from './JobStatusSelect';
import IncotermSelect from 'components/new-ui/composed/selects/IncotermSelect';

export type JobModalFilters = {
    query?: string;
    jobStatus?: JobStatuses[];
    range?: RangeValue<moment.Moment>;
    customerId?: string;
    ownerId?: string;
    createdById?: string;
    teamId?: string;
    incotermCode?: string;
};

type Props = {
    open?: boolean;
    filters: JobModalFilters;
    optionalFilters?: {
        assignedTo?: boolean;
        team?: boolean;
        customer?: boolean;
    };
    onFiltersChange: (path: Partial<JobModalFilters>) => void;
    onClearFilter: (key: JobParamsKeys, value?: string) => void;
    onClearAll: () => void;
    onClose?: () => void;
};

const JobFiltersModal = ({
    open = false,
    optionalFilters,
    onClose,
    onFiltersChange,
    onClearFilter,
    filters,
    onClearAll,
}: Props) => {
    const { jobStatus, query, range, customerId, ownerId, createdById, teamId, incotermCode } = filters;

    return (
        <FiltersModal
            open={open}
            onClose={onClose ?? (() => {})}
            onClearFilters={onClearAll}
            onSubmit={onClose ?? (() => {})}
            header={
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <SearchBar
                        style={{ width: '100%' }}
                        inputPlaceholder="Search jobs"
                        onSearch={(val) => onFiltersChange({ query: val })}
                        value={query}
                    />
                    <CloseButton onClose={onClose ?? (() => {})} />
                </div>
            }
        >
            <Form layout="vertical">
                <Row gutter={40}>
                    <Col span={12}>
                        <Form.Item label="Job Statuses">
                            <JobStatusSelect
                                value={jobStatus}
                                mode="multiple"
                                onChange={(val) => onFiltersChange({ jobStatus: val })}
                                placeholder="Select Statuses"
                            />
                        </Form.Item>
                    </Col>

                    {optionalFilters?.customer && (
                        <Col span={12}>
                            <Form.Item label="Customer">
                                <CustomerSelect
                                    customerId={customerId}
                                    onChange={(id) => onFiltersChange({ customerId: id })}
                                    onClear={() => onClearFilter(JobParamsKeys.CUSTOMER_ID)}
                                />
                            </Form.Item>
                        </Col>
                    )}

                    <Col span={12}>
                        <Form.Item label="Date Range">
                            <RangePicker
                                value={range}
                                allowClear
                                onChange={(values) => onFiltersChange({ range: values })}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Created By">
                            <IndividualSelect
                                individualId={createdById}
                                onChange={(id) => onFiltersChange({ createdById: id })}
                                onClear={() => onClearFilter(JobParamsKeys.CREATED_BY)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Deliery Terms">
                            <IncotermSelect
                                incoterm={incotermCode}
                                onChange={(id) => onFiltersChange({ incotermCode: id })}
                                onClear={() => onClearFilter(JobParamsKeys.INCOTERM_CODE)}
                            />
                        </Form.Item>
                    </Col>

                    {optionalFilters?.assignedTo && (
                        <Col span={12}>
                            <Form.Item label="Assigned To">
                                <IndividualSelect
                                    individualId={ownerId}
                                    onChange={(id) => onFiltersChange({ ownerId: id })}
                                    onClear={() => onClearFilter(JobParamsKeys.OWNER_ID)}
                                    // Show only users who can be assignee
                                    filterIndividuals={(ind) => (ind.teamMemberships?.length ?? 0) > 0}
                                />
                            </Form.Item>
                        </Col>
                    )}

                    {optionalFilters?.team && (
                        <Col span={12}>
                            <Form.Item label="Team">
                                <TeamSelect
                                    teamId={teamId}
                                    onChange={(id) => onFiltersChange({ teamId: id })}
                                    onClear={() => onClearFilter(JobParamsKeys.TEAM_ID)}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </Form>
        </FiltersModal>
    );
};

export default JobFiltersModal;
